import React, {useMemo} from "react";
import {Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {bucketRowsByGroup, colorForGroup, formatEuro, formatEuroCompact, formatPercent} from "./format.js";

/** #5 Warengruppen-Vergleich - horizontale Balken nach Umsatz, mit Anteil. */
export default function GroupComparisonChart({filters, colorMap}) {
    const {data, loading, error} = useStatisticsData("/statistics/by-group", filters);
    const rows = data?.rows || [];

    const chartRows = useMemo(() => {
        const bucketed = bucketRowsByGroup(rows, colorMap, {
            groupKey: "article_group",
            numericKeys: ["quantity", "net_amount", "article_count", "net_share_pct"],
        });
        return bucketed
            .map((row) => ({...row, label: row.article_group || "Ohne Warengruppe"}))
            .sort((a, b) => b.net_amount - a.net_amount);
    }, [rows, colorMap]);

    const height = Math.max(220, chartRows.length * 34);

    return (
        <ChartCard
            title="Warengruppen-Vergleich"
            help="Nettoumsatz je Warengruppe im gewählten Zeitraum, absteigend sortiert. Kleinere Warengruppen werden zu „Sonstige“ zusammengefasst."
            loading={loading}
            error={error}
            empty={!loading && chartRows.length === 0}
            height={height}
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={chartRows} layout="vertical" margin={{top: 4, right: 24, left: 8, bottom: 4}}>
                    <XAxis type="number" tickFormatter={formatEuroCompact} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="label" width={110} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <Tooltip
                        formatter={(value) => [formatEuro(value), "Nettoumsatz"]}
                        labelFormatter={(label, payload) => {
                            const row = payload?.[0]?.payload;
                            return row ? `${label} (${formatPercent(row.net_share_pct)})` : label;
                        }}
                    />
                    <Bar dataKey="net_amount" radius={[0, 6, 6, 0]} barSize={18}>
                        {chartRows.map((row) => (
                            <Cell key={row.label} fill={colorForGroup(colorMap, row.article_group)} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}
